import { FC, createContext, useEffect, useMemo, useReducer } from "react";

import { AuthProviderProps, AuthStateActions, AuthStateProps, UserContextProps } from "./types";
import { HCPUserSubHeaderProps, ManufacturerSubHeaderProps, UserType } from "../../types/users";
import { authContextReducer, initialState } from "./reducer";
import { shutDownAndEndChat } from "src/helpers/tawkto";
import { USER_TYPES } from "src/constants";

export const AuthContext = createContext<UserContextProps>({
  isUserAuthorized: false,
  isManufacturer: false,
  authToken: '',
  defaultView: '',
  userData: null,
  isRestoreCalled: false,
  sub_header: null,
  createSession: (userData: UserType, sub_header: HCPUserSubHeaderProps | ManufacturerSubHeaderProps , authToken: string, defaultView: string,reinvestigation:boolean ,eob_warehouse: boolean,upload_eob: boolean,cep_popup: boolean,new_baa_user_1_2: boolean, outreach: boolean) => {},
  destroySession: () => {},
  restoreSession: () => false,
  updateUserToken: (userToken: string) => {},
  updateUserData: (userData: UserType, defaultView: string,  reinvestigation:boolean, outreach: boolean, pcc_outreach: boolean) => {},
  reinvestigation:false,
  eob_warehouse: false,
  upload_eob: false,
  cep_popup: false,
  new_baa_user_1_2: false,
  outreach: false,
  pcc_outreach: false,
});

const AuthProvider: FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authContextReducer, initialState);
  const authChannel = useMemo(() => new BroadcastChannel("urogen_auth"), []);

  const createSession = (userData: UserType, sub_header: HCPUserSubHeaderProps | ManufacturerSubHeaderProps, authToken: string, defaultView: string,reinvestigation:boolean,eob_warehouse: boolean, upload_eob: boolean, cep_popup: boolean, new_baa_user_1_2:boolean, outreach: boolean, pcc_outreach: boolean) => {
    const data: AuthStateProps = {
      authToken,
      userData,
      sub_header,
      defaultView,
      isUserAuthorized: true,
      isRestoreCalled: false,
      isManufacturer: USER_TYPES.MANUFACTURER.includes(userData.ref_type),
      reinvestigation:reinvestigation,
      eob_warehouse: eob_warehouse,
      upload_eob: upload_eob,
      cep_popup: cep_popup,
      new_baa_user_1_2: new_baa_user_1_2,
      outreach: outreach,
      pcc_outreach: pcc_outreach
    };
    dispatch({
      type: AuthStateActions.SET_SESSION,
      payload: data,
    });

    localStorage.setItem('token', authToken);
    localStorage.setItem('data', JSON.stringify({userData, sub_header,eob_warehouse,upload_eob,cep_popup,defaultView,reinvestigation,new_baa_user_1_2, outreach, pcc_outreach }));
    authChannel.postMessage(userData);
  };

  const destroySession = () => {
    localStorage.clear();
    authChannel.postMessage(null);
    dispatch({ type: AuthStateActions.CLEAR_SESSION });
    shutDownAndEndChat();
  };

  const updateUserData = (userData: UserType, defaultView: string, reinvestigation:boolean, outreach: boolean, pcc_outreach: boolean) => {
    dispatch({
      type: AuthStateActions.UPDATE_USER_DATA,
      payload: { 
        userData, defaultView ,reinvestigation ,isManufacturer:USER_TYPES.MANUFACTURER.includes(userData.ref_type) ? true : false, 
        new_baa_user_1_2: state.new_baa_user_1_2, outreach: outreach, pcc_outreach: pcc_outreach
      },
    })
    localStorage.setItem('data', JSON.stringify({ 
      userData, sub_header: state.sub_header, defaultView, reinvestigation, eob_warehouse: state.eob_warehouse, upload_eob: state.upload_eob, cep_popup: state.cep_popup,
      new_baa_user_1_2: state.new_baa_user_1_2, outreach: outreach, pcc_outreach: pcc_outreach
    }));
    localStorage.setItem("landingPage", defaultView);
    // localStorage.setItem("landingPage", defaultView)
  };

  const updateUserToken = (userToken: string) => {
    dispatch({
      type: AuthStateActions.UPDATE_USER_TOKEN,
      payload: userToken
    });
    localStorage.setItem("token", userToken);
  };
  
  const restoreSession = (): boolean => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('data');
    const data = {} as AuthStateProps;

    if (token && userData) {
      data.isUserAuthorized = true;
      data.authToken = token;
      const dataInLC = JSON.parse(userData);
      data.userData = dataInLC.userData;
      data.sub_header = dataInLC.sub_header;
      data.isManufacturer = USER_TYPES.MANUFACTURER.includes(dataInLC.userData.ref_type);
      data.isRestoreCalled = true;
      data.eob_warehouse = dataInLC.eob_warehouse;
      data.cep_popup = dataInLC.cep_popup;
      data.upload_eob = dataInLC.upload_eob;
      data.defaultView = dataInLC.defaultView;
      data.reinvestigation = dataInLC.reinvestigation;
      data.new_baa_user_1_2  = dataInLC.new_baa_user_1_2;
      data.outreach = dataInLC.outreach;
      data.pcc_outreach = dataInLC.pcc_outreach;
      dispatch({
        type: AuthStateActions.SET_SESSION,
        payload: data,
      });
      return true;
    } else {
      dispatch({
        type: AuthStateActions.SET_SESSION,
        payload: {
          ...initialState,
          isRestoreCalled: true,
        },
      });
      return false;
    }
  };

  useEffect(() => {
    authChannel.onmessage = (_isAuthorized) => {
      if (_isAuthorized.data === null) {
        localStorage.clear();
        dispatch({ type: AuthStateActions.CLEAR_SESSION });
        shutDownAndEndChat();
      }
      else {
        restoreSession();
      }
    }
  }, [authChannel]);

  authChannel.onmessage = (_isAuthorized) => {
    if (_isAuthorized.data === null) {
      localStorage.clear();
      dispatch({ type: AuthStateActions.CLEAR_SESSION });
      shutDownAndEndChat();
    }
    else {
      restoreSession();
    }
  }

  return (
    <AuthContext.Provider value={{
      isUserAuthorized: state.isUserAuthorized,
      isManufacturer: state.isManufacturer,
      authToken: state.authToken,
      defaultView: state.defaultView,
      userData: state.userData,
      isRestoreCalled: state.isRestoreCalled,
      sub_header: state.sub_header,
      createSession,
      destroySession,
      restoreSession,
      updateUserData,
      updateUserToken,
      reinvestigation:state.reinvestigation,
      eob_warehouse: state.eob_warehouse,
      upload_eob: state.upload_eob,
      cep_popup:state.cep_popup,
      new_baa_user_1_2: state.new_baa_user_1_2,
      outreach: state.outreach,
      pcc_outreach: state.pcc_outreach,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
